import {
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  TextCapitalized,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import "../../i18n/i18n";
import { formatDate, getDate } from "../../lib/materialUiFormats";
import { CompanyDeletedDisplayed } from "./companiesDeletedApi";
import {
  companiesDeletedDataAsync,
  selectCompaniesDeletedData,
  selectCompaniesDeletedDataStatus,
} from "./companiesDeletedSlice";
import { useTranslation } from "react-i18next";

export const CompaniesDeletedTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const companiesDeletedData = useAppSelector(selectCompaniesDeletedData);
  const companiesDeletedDataStatus = useAppSelector(
    selectCompaniesDeletedDataStatus,
  );

  const [listDisplayed, setListDisplayed] = useState<
    CompanyDeletedDisplayed[] | null
  >(null);

  const pageName = "companiesDeleted";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      name: "size",
      deleteAt: "size",
      orderBlockedAt: "size",
      providerDeletedAt: "size",
      canReactivateCompany: "size",
    },
  };

  const companiesDeletedDataDisplayed: CompanyDeletedDisplayed[] =
    useMemo(() => {
      const rowsWithId = companiesDeletedData.map((cd) => {
        const canReactivateCompany = t(
          `companiesDeleted.canReactivateCompany.${cd.orderBlockedAt ? "false" : "true"}`,
        );

        return {
          ...cd,
          id: cd.uuid,
          canReactivateCompany,
        };
      });
      return rowsWithId;
    }, [companiesDeletedData, t]);

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const searchBarProps = {
    values: companiesDeletedDataDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  useEffect(() => {
    if (companiesDeletedDataStatus === "idle") {
      void dispatch(companiesDeletedDataAsync());
    }
  }, [companiesDeletedDataStatus, dispatch]);

  const columnsDisplayed: {
    [index in keyof CompanyDeletedDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 200,
      },
      deleteAt: {
        type: "date",
        width: 140,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      canReactivateCompany: {
        type: "string",
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      orderBlockedAt: {
        type: "date",
        width: 140,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      providerDeletedAt: {
        type: "date",
        width: 140,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
    };
  }, []);

  useEffect(() => {
    if (
      companiesDeletedDataDisplayed &&
      companiesDeletedDataStatus === "success"
    ) {
      setListDisplayed(companiesDeletedDataDisplayed);
    }
  }, [companiesDeletedDataDisplayed, companiesDeletedDataStatus]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "companiesDeleted") {
    return null;
  }

  return (
    <StyledContainer>
      <Spacer y={2} />
      <MaterialUiTable<keyof CompanyDeletedDisplayed, CompanyDeletedDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
